/**
 * Sentry initialization — runs once on app boot.
 *
 * No-ops gracefully when REACT_APP_SENTRY_DSN is unset (dev / preview without keys).
 * - 10% trace sampling (plenty for a few-hundred-DAU site).
 * - Replay only fires on errors (buffer mode) with all text masked + media blocked.
 * - beforeSend scrubs `?token=` / `?access_token=` / etc. from URLs before any
 *   event leaves the browser. Combine with server-side scrubbing in Sentry UI
 *   for defence in depth.
 */
import * as Sentry from "@sentry/react";
import React from "react";
import {
    createRoutesFromChildren, matchRoutes, useLocation, useNavigationType,
} from "react-router-dom";

const DSN = process.env.REACT_APP_SENTRY_DSN;
const ENV = process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV;
// Matches the release name created by scripts/sentry-upload.sh — without this,
// source-maps won't bind to errors. The build script populates this env var
// with the short git SHA on every `yarn build:release` invocation.
const RELEASE = process.env.REACT_APP_SENTRY_RELEASE
    ? `hacking-community-web@${process.env.REACT_APP_SENTRY_RELEASE}`
    : undefined;

const SENSITIVE_PARAM_RE = /(token|auth|access_token|id_token|secret|password|api[_-]?key|credential)/i;

function scrubUrl(url) {
    if (typeof url !== "string") return url;
    try {
        const u = new URL(url);
        u.searchParams.forEach((_value, key) => {
            if (SENSITIVE_PARAM_RE.test(key)) u.searchParams.set(key, "[REDACTED]");
        });
        return u.toString();
    } catch {
        return url;
    }
}

function beforeSend(event) {
    if (event?.request?.url) event.request.url = scrubUrl(event.request.url);
    const headers = event?.request?.headers;
    if (headers) {
        for (const k of Object.keys(headers)) {
            if (k.toLowerCase() === "referer" && typeof headers[k] === "string") {
                headers[k] = scrubUrl(headers[k]);
            }
            if (k.toLowerCase() === "authorization") headers[k] = "[REDACTED]";
            if (k.toLowerCase() === "cookie") headers[k] = "[REDACTED]";
        }
    }
    if (Array.isArray(event?.breadcrumbs)) {
        event.breadcrumbs.forEach((b) => {
            if (b?.data && typeof b.data.url === "string") b.data.url = scrubUrl(b.data.url);
        });
    }
    return event;
}

if (DSN) {
    Sentry.init({
        dsn: DSN,
        environment: ENV,
        release: RELEASE,
        sendDefaultPii: false,
        // Don't lock onto exact `release` — we'll wire CI source-maps later.
        integrations: [
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect: React.useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
            Sentry.replayIntegration({
                maskAllText: true,
                maskAllInputs: true,
                blockAllMedia: true,
            }),
        ],
        tracesSampleRate: 0.1,
        replaysSessionSampleRate: 0.0,
        replaysOnErrorSampleRate: 1.0,
        // Only attach distributed-tracing headers to our own backend.
        tracePropagationTargets: [
            /^\//,
            /^https?:\/\/localhost(:\d+)?\//,
            /https:\/\/[^/]*\.preview\.emergentagent\.com\//,
            /https:\/\/(?:www\.)?hacking\.community\//,
        ],
        beforeSend,
        // Skip noisy errors that aren't actionable.
        ignoreErrors: [
            "ResizeObserver loop limit exceeded",
            "ResizeObserver loop completed with undelivered notifications",
            "Non-Error promise rejection captured",
        ],
    });
}

export { Sentry };
